import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { slugify } from '../utils/slugify'; 
import NavLink from './common/NavLink';
import {
    Box, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, CircularProgress
} from '@mui/material';
import { auth } from './Firebase';

// Funkcja kolorująca (konwencja zielona) - identyczna jak w Summary.js
const getTopGradientColor = (value) => {
    if (value === 0) return 'rgba(255, 255, 255, 0.3)';
    const scaledValue = value;
    const grayBase = 255;
    const r = Math.round(grayBase * (1 - scaledValue));
    const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
    const b = Math.round(grayBase * (1 - scaledValue) + 50 * scaledValue);
    const alpha = 0.3 + scaledValue * 0.7;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatChance = (chance, useTilde = false) => {
  if (typeof chance !== 'number' || isNaN(chance)) return '—';

  const percent = chance * 100;

  if (percent < 0.001) return useTilde ? '~0.0%' : '0.0%';
  if (percent > 99.999) return useTilde ? '~100%' : '100.0%';

  const decimals = ((percent < 0.1 && percent > 0) || (percent > 99.9 && percent < 100)) ? 2 : 1;
  const formatted = percent.toFixed(decimals);

  return useTilde && (percent < 0.01 || percent > 99.99)
    ? `~${formatted}%`
    : `${formatted}%`;
};

const Playoff = () => {
    const { competitionId } = useParams();
    const [playoffData, setPlayoffData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPlayoffStats = useCallback(async () => {
        if (!competitionId) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const token = await auth.currentUser?.getIdToken();
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/playoff/${competitionId}`,
                { headers }
            );

            setPlayoffData(response.data);
            setError(null);
        } catch (err) {
            console.error("Błąd pobierania danych barażowych:", err);
            setError(err.response?.data || err.message);
        } finally {
            setLoading(false);
        }
    }, [competitionId]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(() => {
            fetchPlayoffStats();
        });
        return () => unsubscribe();
    }, [fetchPlayoffStats]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
                <CircularProgress sx={{ color: '#3f51b5' }} />
            </Box>
        );
    }

    if (error) return <Typography sx={{ p: 3, color: '#ff5252', textAlign: 'center' }}>Błąd: {error}</Typography>;

    return (
        <Box sx={{ padding: { xs: '0px', sm: '5px', md: '10px' } }}>
            <Typography variant="h5" sx={{
                mb: 3,
                textAlign: 'center',
                fontSize: { xs: '1.0rem', sm: '1.2rem' }
            }}>
                Symulacja Baraży
            </Typography>

            {playoffData.map((template) => {
                const columnCount = 2 + (template.rounds?.length || 0) + 1; // # + Drużyna + rundy + Awans
                const minTableWidth = columnCount * 100;

                return (
                    <Box key={template.templateId} sx={{ mb: 6 }}>
                        <Typography variant="h6" sx={{
                            mb: 1,
                            color: '#ffffff',
                            fontWeight: 600,
							textAlign: 'center',
                            fontSize: { xs: '0.95rem', sm: '1.1rem' },                            
                            pl: 2
                        }}>
                            {template.templateName}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    marginBottom: 1,
                                    width: 'auto',
                                    display: 'inline-block',
                                    overflowX: 'auto',
                                    borderRadius: '12px',
                                    '-webkit-overflow-scrolling': 'touch',
                                    position: 'relative',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                        height: '8px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                        borderRadius: '4px',
                                    },
                                    '&::before': {
                                        content: '"SymuLiga.pl"',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%) rotate(-45deg)',
                                        fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
                                        fontWeight: 'bold',
                                        color: 'rgba(0, 0, 0, 0.15)',
                                        pointerEvents: 'none',
                                        zIndex: 0,
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            >
                                <Table
                                    sx={{
                                        minWidth: minTableWidth,
                                        tableLayout: 'auto',
                                        borderCollapse: 'collapse',
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    top: 0,
                                                    zIndex: 4,
                                                    backgroundColor: 'background.paper',
                                                    width: '10px',
                                                    minWidth: '10px',
                                                    padding: '2px',
                                                    textAlign: 'center',
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    fontWeight: 'bold',
                                                    borderRight: '1px solid rgba(80, 80, 80, 1)',
                                                }}
                                            >
                                                #
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    top: 0,
                                                    zIndex: 5,
                                                    backgroundColor: 'background.paper',
                                                    width: 'auto',
                                                    minWidth: 40,
                                                    padding: '5px',
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                                                }}
                                            >
                                                Drużyna
                                            </TableCell>
                                            {template.rounds.map((round) => (
                                                <TableCell
                                                    key={round.id}
                                                    align="center"
                                                    sx={{
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 2,
                                                        backgroundColor: 'background.paper',
                                                        width: 'auto',
                                                        minWidth: 70,
                                                        padding: '5px',
                                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                        borderBottom: '2px solid rgba(224, 224, 224, 1)',
                                                    }}
                                                >
                                                    {round.name}
                                                </TableCell>
                                            ))}
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 2,
                                                    backgroundColor: 'background.paper',
                                                    width: 'auto',
                                                    minWidth: 70,
                                                    padding: '5px',
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                    borderBottom: '2px solid rgba(224, 224, 224, 1)',
                                                }}
                                            >
                                                Zwycięstwo
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {template.teamStats.map((team, index) => (
                                            <TableRow key={team.teamId}>
                                                <TableCell
                                                    sx={{
                                                        position: 'sticky',
                                                        left: 0,
                                                        zIndex: 2,
                                                        backgroundColor: 'background.paper',
                                                        textAlign: 'center',
                                                        width: '10px',
                                                        minWidth: '10px',
                                                        padding: '2px',
                                                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                        color: 'rgba(255,255,255,0.6)',
                                                        borderRight: '1px solid rgba(80, 80, 80, 1)',
                                                    }}
                                                >
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell
													component={NavLink} 
													to={`/team/${team.teamId}/${slugify(team.teamName)}`}
                                                    sx={{
                                                        position: 'sticky',
                                                        left: 0,
                                                        zIndex: 3,
                                                        backgroundColor: 'background.paper',
                                                        width: 'auto',
                                                        minWidth: 40,
                                                        padding: '5px',
                                                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                                                    }}
                                                >
                                                    {team.teamName}
                                                </TableCell>

                                                {template.rounds.map((round) => {
                                                    const prob = team.appearanceProbabilities[round.id] || 0;
                                                    return (
                                                        <TableCell
                                                            key={round.id}
                                                            align="center"
                                                            sx={{
                                                                width: 'auto',
                                                                minWidth: 70,
                                                                backgroundColor: getTopGradientColor(prob),
                                                                color: 'black',
                                                                fontWeight: 'bold',
                                                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                                padding: '5px',
                                                                textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
                                                                transition: 'background-color 0.3s',
                                                                '@media (hover: hover)': {
                                                                    '&:hover': {
                                                                        backgroundColor: getTopGradientColor(Math.min(prob + 0.1, 1)),
                                                                        transform: 'scale(1.05)',
                                                                    },
                                                                },
                                                            }}
                                                        >
															{formatChance(prob, false)}
                                                            
                                                        </TableCell>
                                                    );
                                                })}

                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        width: 'auto',
                                                        minWidth: 70,
                                                        backgroundColor: getTopGradientColor(team.ultimateWinProbability || 0),
                                                        color: 'black',
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                        padding: '5px',
                                                        textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
                                                        transition: 'background-color 0.3s',
                                                        '@media (hover: hover)': {
                                                            '&:hover': {
                                                                backgroundColor: getTopGradientColor(Math.min((team.ultimateWinProbability || 0) + 0.1, 1)),
                                                                transform: 'scale(1.05)',
                                                            },
                                                        },
                                                    }}
                                                >
													{formatChance(team.ultimateWinProbability || 0, false)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default Playoff;
